// © Thibault Sabathier, 2026. frontend/src/utils/httpErrors.js
//
// Traduction des statuts HTTP « structurels » en erreurs applicatives typées.
//
// ── POURQUOI UN MODULE À PART ──────────────────────────────────────────────
// Un 413 ne vient pas forcément d'Express : nginx coupe la requête sur
// `client_max_body_size` AVANT que l'application ne la voie, et répond une page
// HTML. Le corps n'est donc pas du JSON, aucun `code` applicatif n'y figure, et
// tout écran qui lit `error.response.data.code` retombe sur son message
// générique — c'est précisément ce qu'a vécu la publication multi-photos du
// 15/08 : « Une erreur est survenue lors de la publication », sans autre indice,
// et rien dans les logs applicatifs puisque la requête n'était jamais arrivée.
//
// Le mapping vit ici, hors des intercepteurs, pour deux raisons : il est
// partagé par `apiClient` et `recruiterApiClient` (deux systèmes d'auth
// distincts, même besoin), et il est testable sans DOM ni axios — le runner du
// dépôt tourne en `environment: 'node'`.
//
// ── INDÉPENDANT DES GARDES CÔTÉ CLIENT ─────────────────────────────────────
// Cette traduction ne remplace PAS le contrôle de budget de la sélection de
// photos : elle en est la défense de secours. Le client peut être contourné,
// un autre formulaire peut envoyer un fichier, l'overhead multipart peut
// varier, ou une autre limite serveur peut se déclencher. Le 413 doit rester
// compréhensible même quand rien ne l'avait anticipé.

/** Corps de requête refusé — par nginx (`client_max_body_size`) ou par multer. */
export const CODE_PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE'

/**
 * Construit l'erreur typée correspondant à une réponse HTTP, ou `null` si le
 * statut ne relève pas de ce module (l'appelant poursuit alors sa chaîne).
 *
 * @param {{status: number, data: any}} response
 * @returns {Error|null} Erreur portant `.status`, `.code` et `.response`.
 */
export const toTypedHttpError = (response) => {
  if (!response || response.status !== 413) return null

  // Le message du backend n'est repris que s'il est exploitable. Sur un 413
  // nginx, `data` est une page HTML complète : l'afficher à l'utilisateur
  // serait pire que le message générique.
  const backendMessage = typeof response.data?.message === 'string' ? response.data.message : null

  const error = new Error(backendMessage || 'Contenu trop volumineux')
  error.status = 413
  error.code = CODE_PAYLOAD_TOO_LARGE
  error.response = response
  return error
}

/** Un écran peut ainsi tester le code sans connaître le statut. */
export const isPayloadTooLarge = (error) => error?.code === CODE_PAYLOAD_TOO_LARGE

/**
 * Lit le motif de refus renvoyé par le backend, pour l'afficher à la place du
 * message générique d'un écran.
 *
 * ── POURQUOI `message` AVANT `error` ───────────────────────────────────────
 * Les deux formes coexistent dans l'API. Les erreurs MÉTIER répondent
 * `{ success: false, error: '…' }` ; les GARDES transverses (le 403
 * PASSWORD_CHANGE_REQUIRED de `requirePasswordChangeDone`, MODULE_DISABLED,
 * INVALID_GOAL…) répondent `{ code, message }` — sans aucun champ `error`.
 *
 * Un écran qui ne lisait que `.error` retombait donc systématiquement sur son
 * message générique face à une garde. C'est ce qui a rendu illisible le blocage
 * du 26/08 sur ecoles-billieres : l'utilisateur voyait « Erreur lors de
 * l'enregistrement. Réessayez. » là où le serveur disait précisément « Vous
 * devez changer votre mot de passe provisoire avant de continuer. » — et
 * réessayait donc indéfiniment, ce qui ne pouvait pas marcher.
 *
 * @param {{data?: any}|null|undefined} response  `error.response` (axios).
 * @param {string} fallback  Message de l'écran si le corps n'est pas exploitable.
 * @returns {string}
 */
export const readServerMessage = (response, fallback) => {
  const data = response?.data

  // Même prudence que sur le 413 nginx : le corps peut être une page HTML, un
  // nombre ou un objet. On n'affiche QUE des chaînes non vides — le message
  // générique de l'écran vaut mieux qu'un `[object Object]` rendu à l'écran.
  const candidates = [data?.message, data?.error]
  const usable = candidates.find((value) => typeof value === 'string' && value.trim() !== '')

  return usable ?? fallback
}
